import { FC } from 'react';
import classNames from 'classnames';
import GridTextField from '@app/shared/components/grid-controls/grid-text-field/GridTextField';
import { percentageFieldWithoutPlaceholderFormattingProps } from '@app/shared/components/form-controls/form-field/form-field-patterns';
import { positiveOrNullValidator } from '@app/core/validations/hook-forms/validators';
import { TrancheKey } from '@app/shared/models/contracts/enums/shared-enums';
import { OverrideField } from './useDiscountRateOverride';
import {
  getCellOverrideClass,
  getInstrumentOverrideIndex,
  CaseStructureItem,
  getOriginalValue,
} from './discountRateOverrideUtils';
import { CaseDto } from '@app/shared/models/contracts/project-dto';
import { useAppSelector } from '@app/core/hooks/redux-hooks';

interface OverrideCellsProps {
  trancheId: Nullable<string>;
  keyPrefix: string;
  casesStructure: CaseStructureItem[];
  findSortedCaseIndex: (caseId: string) => number;
  instrumentId: string;
  field: OverrideField;
  initializedCases: CaseDto[];
}

const OverrideCells: FC<OverrideCellsProps> = ({
  trancheId,
  keyPrefix,
  casesStructure,
  findSortedCaseIndex,
  instrumentId,
  field,
  initializedCases,
}) => {
  const results = useAppSelector((state) => state.pwerm2Calculation.calculatedResults);

  const getFieldName = (
    caseIndex: number,
    forecastIndex: number,
    instrumentIdx: number
  ): string => {
    const base = `cases[${caseIndex}].forecasts[${forecastIndex}].discountRateOverrides[${instrumentIdx}].${field}`;
    if (trancheId) {
      return `${base}.${trancheId}`;
    }
    if (field === 'exitDistributionByTrancheDiscountRateOverrides') {
      return `${base}.${TrancheKey.DefaultTrancheKey}`;
    }
    return base;
  };

  return (
    <>
      {casesStructure.map((caseItem) =>
        caseItem.forecasts.map((forecast, forecastIndex) => {
          const caseIndex = findSortedCaseIndex(caseItem.caseId);
          const instrumentIdx = getInstrumentOverrideIndex(
            initializedCases,
            caseIndex,
            forecastIndex,
            instrumentId
          );

          const originalValue = getOriginalValue(
            caseItem.caseId,
            forecast.forecastId,
            field,
            instrumentId,
            results,
            trancheId
          );

          const placeholder = originalValue != null ? `${originalValue.toFixed(2)}%` : '0.00%';

          return (
            <td
              key={forecast.forecastId + '-' + keyPrefix}
              className={classNames({
                'table-primary__cell--vertical-separator':
                  caseItem.forecasts.length - 1 === forecastIndex,
              })}>
              {instrumentIdx !== -1 && (
                <GridTextField
                  className={getCellOverrideClass(
                    initializedCases,
                    caseIndex,
                    forecastIndex,
                    instrumentId,
                    trancheId,
                    field
                  )}
                  name={getFieldName(caseIndex, forecastIndex, instrumentIdx)}
                  placeholder={placeholder}
                  {...percentageFieldWithoutPlaceholderFormattingProps}
                  rules={positiveOrNullValidator(true)}
                />
              )}
            </td>
          );
        })
      )}
    </>
  );
};

export default OverrideCells;
